import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import Header from '../Header';
import AdminNav from './AdminNav';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';

const DAY_OPTIONS = [7, 30, 90];

/** Peak hour is highlighted so an admin can spot it at a glance without hovering every bar. */
function peakHour(hourlyUsage) {
    return hourlyUsage.reduce((best, h) => (h.avgOnlineUsers > (best?.avgOnlineUsers ?? -1) ? h : best), null);
}

export default function AdminUsageStatsScreen() {
    const { authFetch } = useAuth();
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await authFetch(`/admin/stats/usage?days=${days}`);
            setData(result);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'No se pudieron cargar las estadísticas de uso.');
        } finally {
            setIsLoading(false);
        }
    }, [authFetch, days]);

    useEffect(() => {
        load();
    }, [load]);

    const hourlyUsage = data?.hourlyUsage ?? [];
    const maxAvg = Math.max(1, ...hourlyUsage.map((h) => h.avgOnlineUsers));
    const best = peakHour(hourlyUsage);
    const hasAnySamples = hourlyUsage.some((h) => h.sampleCount > 0);

    return (
        <>
            <Header title="Estadísticas de uso" showSettings={false} />
            <AdminNav />
            <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
                <Card elevation={2}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                            <div>
                                <Typography variant="h6">Jugadores en línea por hora del día</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Media de jugadores conectados en cada hora (zona horaria de España), a partir de
                                    muestras periódicas del backend.
                                </Typography>
                            </div>
                            <TextField
                                select
                                size="small"
                                label="Periodo"
                                value={days}
                                onChange={(e) => setDays(Number(e.target.value))}
                                sx={{ minWidth: 140 }}
                            >
                                {DAY_OPTIONS.map((d) => (
                                    <MenuItem key={d} value={d}>Últimos {d} días</MenuItem>
                                ))}
                            </TextField>
                        </Stack>

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : !hasAnySamples ? (
                            <Alert severity="info">
                                Todavía no hay suficientes muestras para este periodo. El servidor toma una muestra
                                cada pocos minutos, vuelve a comprobarlo más tarde.
                            </Alert>
                        ) : (
                            <>
                                {best && best.avgOnlineUsers > 0 && (
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Hora de mayor conectividad: <strong>{String(best.hour).padStart(2, '0')}:00</strong>{' '}
                                        (media de {best.avgOnlineUsers} jugadores en línea).
                                    </Typography>
                                )}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        gap: '2px',
                                        height: 220,
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                        pb: 0.5,
                                    }}
                                >
                                    {hourlyUsage.map((h) => {
                                        const heightPct = (h.avgOnlineUsers / maxAvg) * 100;
                                        const isPeak = best && h.hour === best.hour && h.avgOnlineUsers > 0;
                                        return (
                                            <Tooltip
                                                key={h.hour}
                                                title={`${String(h.hour).padStart(2, '0')}:00 — media ${h.avgOnlineUsers}, pico ${h.maxOnlineUsers} (${h.sampleCount} muestras)`}
                                            >
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        height: `${Math.max(heightPct, 2)}%`,
                                                        bgcolor: isPeak ? 'primary.main' : 'primary.light',
                                                        borderRadius: '2px 2px 0 0',
                                                        minWidth: 4,
                                                        transition: 'height 0.2s ease',
                                                    }}
                                                />
                                            </Tooltip>
                                        );
                                    })}
                                </Box>
                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                                    {[0, 6, 12, 18, 23].map((h) => (
                                        <Typography key={h} variant="caption" color="text.secondary">
                                            {String(h).padStart(2, '0')}:00
                                        </Typography>
                                    ))}
                                </Stack>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </>
    );
}
